import React from 'react'
import PropTypes from 'prop-types'
import { useRouteMatch } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import Error from 'components/error'
import { PageHeader } from 'components/page-header'
import useSettingsStyles from '../settings.styles'
import { SettingsSection } from '../settings-components'
import { useFollowUpAutomations } from './api-hooks'
import AutomationRuleRows, { UpgradeBanner } from './automation-rule-rows'
import ResultsSection from './results-section'
import { describeSchedule } from './constants'
import useAutomationActions from './use-automation-actions'
import useStyles from './styles'

export function FollowUpAutomationsView({
  automations,
  editableTriggers,
  crumbs,
  savingTrigger,
  upgradeLoading,
  onToggle,
  onEdit,
  onUpgrade,
}) {
  const classes = useStyles()
  const settingsStyles = useSettingsStyles()
  const {
    available,
    upgradeMessage,
    dailyCap,
    sentToday,
    sendSchedule,
    holdoutPercent,
    results,
    rules,
  } = automations

  return (
    <>
      <PageHeader title="Follow-up Automations" crumbs={crumbs} />
      <Box className={settingsStyles.contentContainer}>
        {!available ? (
          <UpgradeBanner
            message={upgradeMessage}
            loading={upgradeLoading}
            onUpgrade={onUpgrade}
          />
        ) : null}

        <SettingsSection
          eyebrow="Automations"
          subtitle={`You approve the message and who qualifies when you turn an automation on, and again if you change its timing. After that, messages send automatically on ${describeSchedule(
            sendSchedule,
          )}, with a maximum of ${dailyCap} messages per day across all automations.`}
          classes={settingsStyles}
        >
          <AutomationRuleRows
            available={available}
            rules={rules}
            editableTriggers={editableTriggers}
            savingTrigger={savingTrigger}
            onToggle={onToggle}
            onEdit={onEdit}
          />
        </SettingsSection>

        <ResultsSection results={results} holdoutPercent={holdoutPercent} />

        <Typography component="p" className={classes.footerNote}>
          {sentToday >= dailyCap
            ? `Today’s limit of ${dailyCap} automated follow-ups is reached. More go out on the next scheduled send.`
            : `${sentToday} of ${dailyCap} automated follow-ups sent today.`}
        </Typography>
      </Box>
    </>
  )
}

FollowUpAutomationsView.propTypes = {
  editableTriggers: PropTypes.arrayOf(PropTypes.string).isRequired,
  automations: PropTypes.shape({
    available: PropTypes.bool.isRequired,
    upgradeMessage: PropTypes.string.isRequired,
    dailyCap: PropTypes.number.isRequired,
    sentToday: PropTypes.number.isRequired,
    sendSchedule: PropTypes.string.isRequired,
    holdoutPercent: PropTypes.number.isRequired,
    results: PropTypes.object.isRequired,
    rules: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  crumbs: PropTypes.arrayOf(PropTypes.object),
  savingTrigger: PropTypes.string,
  upgradeLoading: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onUpgrade: PropTypes.func.isRequired,
}

export default function FollowUpAutomations() {
  const { path } = useRouteMatch()
  const { data, loading, error, refetch } = useFollowUpAutomations()
  const actions = useAutomationActions({ data, refetch })

  if (loading && !data) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />

  const automations = data?.outreachAutomations
  if (!automations) return <Error statusCode={404} />

  const crumbs = [
    {
      label: 'Settings',
      to: path
        .split('/')
        .slice(0, 3)
        .join('/'),
    },
  ]

  return (
    <>
      <FollowUpAutomationsView
        automations={automations}
        editableTriggers={data.outreachTemplates.map(({ trigger }) => trigger)}
        crumbs={crumbs}
        savingTrigger={actions.savingTrigger}
        upgradeLoading={actions.upgradeLoading}
        onToggle={actions.onToggle}
        onEdit={actions.onEdit}
        onUpgrade={actions.onUpgrade}
      />
      {actions.dialogs}
    </>
  )
}
